import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { Job, JobStatus, SiteActivityItem, SiteActivityKind, User } from '../types';

/* -------------------------------------------------------------------------- */
/* Appointments                                                               */
/* -------------------------------------------------------------------------- */

export type AppointmentCategory = 'CUSTOMER_ESTIMATE' | 'CUSTOMER_JOB' | 'CONTRACTOR' | 'WORK';

export interface PlannerAppointment {
  jobId: string;
  title: string;
  clientName: string;
  phone: string;
  email: string;
  address: string;
  serviceType: string;
  status: string;
  category: AppointmentCategory;
  categoryLabel: string;
  start: Date;
  end: Date;
  durationMins: number;
  others: string[];
  notes: string;
  /** False once the work is finished — still shown on the planner, never alerted. */
  alertable: boolean;
}

export const CATEGORY_STYLE: Record<AppointmentCategory, {
  label: string;
  card: string;
  border: string;
  chip: string;
  dot: string;
}> = {
  CUSTOMER_ESTIMATE: {
    label: 'Estimate visit',
    card: 'bg-amber-50',
    border: 'border-amber-300',
    chip: 'bg-amber-100 text-amber-800',
    dot: 'bg-amber-500',
  },
  CUSTOMER_JOB: {
    label: 'Customer job',
    card: 'bg-emerald-50',
    border: 'border-emerald-300',
    chip: 'bg-emerald-100 text-emerald-800',
    dot: 'bg-emerald-600',
  },
  CONTRACTOR: {
    label: 'Contractor',
    card: 'bg-sky-50',
    border: 'border-sky-300',
    chip: 'bg-sky-100 text-sky-800',
    dot: 'bg-sky-600',
  },
  WORK: {
    label: 'Crew / work',
    card: 'bg-stone-50',
    border: 'border-stone-300',
    chip: 'bg-stone-200 text-stone-700',
    dot: 'bg-stone-500',
  },
};

const FINISHED_STATUSES = new Set<string>([
  JobStatus.COMPLETED,
  JobStatus.INVOICED,
  JobStatus.PAID,
]);

export function parseScheduledDate(raw?: string | null): Date | null {
  if (!raw) return null;
  const text = String(raw);
  // Date-only values are all-day placeholders; the rest of the app anchors them at 08:00.
  const parsed = text.length === 10 ? new Date(`${text}T08:00:00`) : new Date(text);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function minutesFromMidnight(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function dayKey(date: Date): string {
  const two = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}`;
}

export function formatClock(date: Date): string {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * Turn a job into a planner appointment. Returns null for anything that is not
 * a real appointment with another person: no date, deleted records, or an
 * internal reminder with nobody attached.
 */
export function toAppointment(job: Job): PlannerAppointment | null {
  if ((job as any).isDeleted) return null;
  const start = parseScheduledDate(job.scheduledDate);
  if (!start) return null;

  const commitments = (job.commitments || []).filter(c => !!c.workerName);
  const others = [
    ...(job.clientName ? [job.clientName] : []),
    ...commitments.map(c => c.workerName),
  ].filter((v, i, arr) => !!v && arr.indexOf(v) === i);

  // "An appointment with another person" is the hard requirement for this widget.
  if (others.length === 0) return null;

  const contractorCommitment = commitments.some(c => /contractor|sub-?contract/i.test(String(c.role || '')));
  let category: AppointmentCategory;
  if (contractorCommitment || String((job as any).source || '') === 'arborlink') {
    category = 'CONTRACTOR';
  } else if (job.status === JobStatus.ESTIMATING || job.status === JobStatus.PENDING) {
    category = 'CUSTOMER_ESTIMATE';
  } else if (job.clientName) {
    category = 'CUSTOMER_JOB';
  } else {
    category = 'WORK';
  }

  const durationMins = Math.max(
    15,
    Number((job as any).estimatedDurationMins) || (category === 'CUSTOMER_ESTIMATE' ? 45 : 120),
  );
  const end = new Date(start.getTime() + durationMins * 60_000);

  return {
    jobId: job.id,
    title: job.clientName || commitments[0]?.workerName || 'Scheduled work',
    clientName: job.clientName || '—',
    phone: job.phone || '',
    email: job.email || '',
    address: job.address || '',
    serviceType: job.serviceType || 'Tree care',
    status: String(job.status || ''),
    category,
    categoryLabel: CATEGORY_STYLE[category].label,
    start,
    end,
    durationMins,
    others,
    notes: (job as any).notes || (job as any).estimateNotes || '',
    alertable: !FINISHED_STATUSES.has(String(job.status || '')) && start.getTime() > Date.now(),
  };
}

export function buildAppointments(jobs: Job[]): PlannerAppointment[] {
  return jobs
    .map(toAppointment)
    .filter((a): a is PlannerAppointment => !!a)
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}

export function appointmentsOnDay(jobs: Job[], day: Date): PlannerAppointment[] {
  const key = dayKey(day);
  return buildAppointments(jobs).filter(a => dayKey(a.start) === key);
}

/* -------------------------------------------------------------------------- */
/* Site activity feed                                                         */
/* -------------------------------------------------------------------------- */

const SEEN_KEY = 'gt_activity_seen_v1';
const ALERTED_KEY = 'gt_appointment_alerts_v1';
const PREFS_KEY = 'gt_ops_alert_prefs_v1';
const DAY_MS = 24 * 60 * 60 * 1000;
const OWNER_EMAILS = new Set([
  'mrgreen@greentreearboriculture.com',
  'brandtmgreen@gmail.com',
]);

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage can be blocked by browser privacy settings */
  }
}

function showDesktopNotification(title: string, body: string, tag?: string) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const n = new Notification(title, {
      body,
      icon: '/icon-192x192.png',
      badge: '/icon-192x192.png',
      tag,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    setTimeout(() => n.close(), 15_000);
  } catch {
    /* desktop notifications are an enhancement, never a failure path */
  }
}

const WORKSHEET_LABELS: Record<string, string> = {
  'job-briefing': 'Job briefing',
  risk: 'Risk assessment',
  health: 'Tree health survey',
  media: 'Media capture',
  'work-order': 'Work order',
  consultation: 'Consultation',
  survey: 'Tree survey',
  storm: 'Storm assessment',
  safety: 'Safety checklist',
  equipment: 'Equipment check',
  'cloud-worksheet': 'Cloud worksheet',
};

function activityTimestamp(data: any): string | null {
  const value = data?.createdAt || data?.submittedAt || data?.estimateScheduledAt || data?.lastLogin;
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function trim(text: unknown, max = 120): string {
  const value = typeof text === 'string' ? text.trim() : '';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function mapUser(id: string, data: any): SiteActivityItem | null {
  const createdAt = activityTimestamp(data);
  if (!createdAt) return null;
  const name = trim(data.name) || trim(data.email) || 'New account';
  const account = trim(data.accountType || data.role) || 'account';
  return {
    id: `ACCOUNT:${id}`,
    kind: 'ACCOUNT',
    title: `${name} created an account`,
    detail: `${account} account${data.email ? ` · ${trim(data.email, 80)}` : ''}`,
    actorName: name,
    actorEmail: trim(data.email, 80) || undefined,
    createdAt,
    route: 'access',
  };
}

function mapLead(id: string, data: any): SiteActivityItem | null {
  if (data?.isDeleted) return null;
  const status = String(data?.status || '');
  if (status !== JobStatus.LEAD && status !== JobStatus.PENDING) return null;
  const createdAt = activityTimestamp(data);
  if (!createdAt) return null;
  const name = trim(data.clientName) || trim(data.name) || 'Website visitor';
  const service = trim(data.serviceType) || 'Tree care';
  const where = trim(data.address, 90);
  return {
    id: `ESTIMATE_REQUEST:${id}`,
    kind: 'ESTIMATE_REQUEST',
    title: `New estimate request from ${name}`,
    detail: `${service}${where ? ` · ${where}` : ''}${data.source ? ` · ${trim(data.source, 30)}` : ''}`,
    actorName: name,
    actorEmail: trim(data.email, 80) || undefined,
    createdAt,
    route: 'leads',
  };
}

function mapJob(id: string, data: any): SiteActivityItem | null {
  if (data?.isDeleted) return null;
  if (String(data?.source || '') !== 'self-booked') return null;
  const createdAt = activityTimestamp(data);
  if (!createdAt) return null;
  const name = trim(data.clientName, 90) || 'Website visitor';
  const when = parseScheduledDate(data.scheduledDate);
  return {
    id: `BOOKING:${id}`,
    kind: 'BOOKING',
    title: `${name} booked an estimate online`,
    detail: when
      ? `${when.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} at ${formatClock(when)}${data.address ? ` · ${trim(data.address, 70)}` : ''}`
      : 'Self-booked from the website',
    actorName: name,
    actorEmail: trim(data.email, 80) || undefined,
    createdAt,
    route: 'schedule',
  };
}

function mapWorksheet(id: string, data: any): SiteActivityItem | null {
  const createdAt = activityTimestamp(data);
  if (!createdAt) return null;
  const formId = String(data?.formId || '');
  const label = WORKSHEET_LABELS[formId] || 'Field form';
  const payload = data?.data || {};
  const name = trim(payload.name || payload.clientName || payload.ownerName || payload.submittedBy, 90);
  const address = trim(payload.address || payload.siteAddress || payload.propertyAddress, 80);
  return {
    id: `WORKSHEET:${id}`,
    kind: 'WORKSHEET',
    title: `${label} submitted`,
    detail: [name, address].filter(Boolean).join(' · ') || 'Submitted from the public Worksheetz tool',
    actorName: name || undefined,
    createdAt,
    route: 'jobs',
  };
}

const NOTIFY_COPY: Record<SiteActivityKind, { type: 'ACCOUNT' | 'ESTIMATE' | 'ACTIVITY' }> = {
  ACCOUNT: { type: 'ACCOUNT' },
  ESTIMATE_REQUEST: { type: 'ESTIMATE' },
  BOOKING: { type: 'ESTIMATE' },
  WORKSHEET: { type: 'ACTIVITY' },
};

export interface OperationsAlerts {
  activity: SiteActivityItem[];
  unreadActivityIds: string[];
  markActivityRead: (id: string) => void;
  markAllActivityRead: () => void;
  alertsEnabled: boolean;
  setAlertsEnabled: (enabled: boolean) => void;
  desktopPermission: NotificationPermission | 'unsupported';
  requestDesktopPermission: () => Promise<void>;
  nextAppointment: PlannerAppointment | null;
  alertableToday: number;
}

export function useOperationsAlerts(params: {
  jobs: Job[];
  user: User | null;
  onNotify: (payload: {
    title: string;
    message: string;
    type: 'APPLICATION' | 'MISSION' | 'SYSTEM' | 'APPOINTMENT' | 'ESTIMATE' | 'ACCOUNT' | 'ACTIVITY';
    meta?: any;
  }) => void;
}): OperationsAlerts {
  const { jobs, user, onNotify } = params;
  // The brief is explicit: only the owner is alerted, and nothing is sent to
  // customers, contractors or other staff. This is enforced here, not just
  // hidden in the UI. Keep the email list aligned with AuthView's owners.
  const isOwner =
    user?.role === 'staff' &&
    user?.staffRole === 'admin' &&
    OWNER_EMAILS.has(String(user?.email || '').toLowerCase());

  const [activity, setActivity] = useState<SiteActivityItem[]>([]);
  const [seen, setSeen] = useState<string[]>(() => readJSON<string[]>(SEEN_KEY, []));
  const [alertsEnabled, setAlertsEnabledState] = useState<boolean>(() => {
    const prefs = readJSON<{ appointmentAlerts?: boolean }>(PREFS_KEY, {});
    return prefs.appointmentAlerts !== false;
  });
  const [desktopPermission, setDesktopPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  const notifyRef = useRef(onNotify);
  notifyRef.current = onNotify;
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const alertsEnabledRef = useRef(alertsEnabled);
  alertsEnabledRef.current = alertsEnabled;
  const alertedRef = useRef<Set<string>>(new Set(readJSON<string[]>(ALERTED_KEY, [])));

  useEffect(() => {
    writeJSON(SEEN_KEY, seen.slice(0, 600));
  }, [seen]);

  useEffect(() => {
    writeJSON(PREFS_KEY, { appointmentAlerts: alertsEnabled });
  }, [alertsEnabled]);

  const mergeItems = useCallback((incoming: SiteActivityItem[]) => {
    if (!incoming.length) return;
    setActivity(prev => {
      const byId = new Map(prev.map(item => [item.id, item]));
      incoming.forEach(item => byId.set(item.id, item));
      const cutoff = Date.now() - 14 * DAY_MS;
      return Array.from(byId.values())
        .filter(item => new Date(item.createdAt).getTime() >= cutoff)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, 80);
    });
  }, []);

  const announce = useCallback((item: SiteActivityItem) => {
    const copy = NOTIFY_COPY[item.kind];
    notifyRef.current({
      title: item.title,
      message: item.detail,
      type: copy.type,
      meta: { activityId: item.id, route: item.route, kind: item.kind },
    });
    showDesktopNotification(item.title, item.detail, item.id);
  }, []);

  useEffect(() => {
    if (!isOwner) {
      setActivity([]);
      return;
    }

    const unsubscribers: Array<() => void> = [];

    /**
     * Subscribe to a collection ordered by its timestamp. The first snapshot is
     * treated as history (no toast storm on login); later documents announce
     * themselves. Firestore can replay an old snapshot after a reconnect, so a
     * 5-minute freshness check prevents duplicate alerts.
     */
    const subscribe = (
      name: string,
      timeField: string,
      mapper: (id: string, data: any) => SiteActivityItem | null,
    ) => {
      const known = new Set<string>();
      let first = true;
      let unsubscribe = () => {};
      try {
        const q = query(collection(db, name), orderBy(timeField, 'desc'), limit(40));
        unsubscribe = onSnapshot(q, snapshot => {
          const items: SiteActivityItem[] = [];
          snapshot.docs.forEach(docSnap => {
            const item = mapper(docSnap.id, docSnap.data() || {});
            if (!item) return;
            items.push(item);
            if (!known.has(docSnap.id)) {
              known.add(docSnap.id);
              if (!first && Date.now() - new Date(item.createdAt).getTime() < 5 * 60 * 1000) {
                announce(item);
              }
            }
          });
          mergeItems(items);
          if (first) {
            first = false;
            const olderThanADay = items
              .filter(item => Date.now() - new Date(item.createdAt).getTime() > DAY_MS)
              .map(item => item.id);
            if (olderThanADay.length) {
              setSeen(prev => Array.from(new Set([...prev, ...olderThanADay])));
            }
          }
        }, error => {
          console.warn(`Site activity listener unavailable for ${name}:`, error?.message || error);
        });
      } catch (error: any) {
        console.warn(`Site activity listener could not start for ${name}:`, error?.message || error);
      }
      unsubscribers.push(unsubscribe);
    };

    subscribe('users', 'createdAt', mapUser);
    subscribe('leads', 'createdAt', mapLead);
    subscribe('jobs', 'createdAt', mapJob);
    subscribe('worksheetz_submissions', 'submittedAt', mapWorksheet);

    return () => unsubscribers.forEach(unsub => unsub());
  }, [isOwner, announce, mergeItems]);

  const markActivityRead = useCallback((id: string) => {
    setSeen(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const markAllActivityRead = useCallback(() => {
    setSeen(prev => Array.from(new Set([...prev, ...activity.map(item => item.id)])).slice(0, 600));
  }, [activity]);

  const unreadActivityIds = useMemo(
    () => activity.filter(item => !seen.includes(item.id)).map(item => item.id),
    [activity, seen],
  );

  const setAlertsEnabled = useCallback((enabled: boolean) => {
    setAlertsEnabledState(enabled);
  }, []);

  const requestDesktopPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      setDesktopPermission('unsupported');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setDesktopPermission(permission);
      if (permission === 'granted') {
        showDesktopNotification(
          'GreenTree alerts armed',
          'Appointment reminders and site activity will appear here while the dashboard is open.',
          'greentree-alerts-armed',
        );
      }
    } catch {
      setDesktopPermission(Notification.permission);
    }
  }, []);

  /* One-hour appointment alerts ------------------------------------------- */
  useEffect(() => {
    if (!isOwner) return;

    const check = () => {
      if (!alertsEnabledRef.current) return;
      const now = Date.now();
      buildAppointments(jobsRef.current).forEach(appointment => {
        if (!appointment.alertable) return;
        const msUntil = appointment.start.getTime() - now;
        // Alert window: any time between now and one hour out. The key is
        // persisted, so a reload or a second tab cannot repeat the chime.
        if (msUntil <= 0 || msUntil > 60 * 60 * 1000) return;

        const key = `${appointment.jobId}|${appointment.start.toISOString()}`;
        if (alertedRef.current.has(key)) return;
        alertedRef.current.add(key);
        writeJSON(ALERTED_KEY, Array.from(alertedRef.current).slice(-300));

        const minutes = Math.max(1, Math.round(msUntil / 60_000));
        const title = minutes >= 55 ? 'Appointment in about 1 hour' : `Appointment in ${minutes} min`;
        const message = `${appointment.title} · ${appointment.categoryLabel} at ${formatClock(appointment.start)}${
          appointment.address ? ` · ${appointment.address}` : ''
        }`;
        notifyRef.current({
          title,
          message,
          type: 'APPOINTMENT',
          meta: { jobId: appointment.jobId, startsAt: appointment.start.toISOString() },
        });
        showDesktopNotification(title, message, key);
      });
    };

    check();
    const timer = window.setInterval(check, 30_000);
    return () => window.clearInterval(timer);
  }, [isOwner, alertsEnabled]);

  const nextAppointment = useMemo(() => {
    const now = Date.now();
    return buildAppointments(jobs).find(a => a.alertable && a.start.getTime() > now) || null;
  }, [jobs]);

  const alertableToday = useMemo(() => {
    const today = dayKey(new Date());
    return buildAppointments(jobs).filter(a => a.alertable && dayKey(a.start) === today).length;
  }, [jobs]);

  return {
    activity,
    unreadActivityIds,
    markActivityRead,
    markAllActivityRead,
    alertsEnabled,
    setAlertsEnabled,
    desktopPermission,
    requestDesktopPermission,
    nextAppointment,
    alertableToday,
  };
}
